import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import sv from '@angular/common/locales/sv';
import {
    Component,
    ElementRef,
    HostListener,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import { SbPrintService } from '@services';
import { SessionQuery } from './core/state/session/session.query';
import { slideInAnimation } from './modal-transitions';
import { UiQuery } from './shared/state/ui/ui.query';
import { SessionService } from './core/state/session/session.service';
import { InstrumentsService } from './shared/state/instruments/instruments.service';
import { combineLatest, from, fromEvent, ReplaySubject, Subject } from 'rxjs';
import { AccountsService } from './shared/state/accounts/accounts.service';
import { AppConfigQuery } from './core/state/app-config/app-config.query';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { SettingsQuery } from './features/settings/state/settings.query';
import { UiService } from './shared/state/ui/ui.service';
import { LocaleService } from './shared/services/locale/locale.service';
import { CategoryService } from './pages/authenticated/categories/state/categories.service';
import { KeycloakService } from 'keycloak-angular';
import { UserSettingsQuery } from './features/user-settings/state/user-settings.query';
import { Router } from '@angular/router';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [slideInAnimation]
})
export class AppComponent implements OnInit, OnDestroy {
    disconnected = false;
    loggedIn$;
    unsubscribe$ = new Subject();
    isDialogActive: boolean = false;
    isModalActive: boolean = false;

    /** fix for using angular router with simple html content with ag-grid for links */
    @HostListener('document:click', ['$event'])
    public handleClick(event: Event): void {
        if (event.target instanceof HTMLAnchorElement) {
            const element = event.target as HTMLAnchorElement;
            if (element.className === 'ag-table-link') {
                event.preventDefault();
                const route = element?.getAttribute('href');
                if (route) {
                    this._router.navigateByUrl(route);
                }
            }
        }
    }

    @ViewChild('layoutContainer') set layoutContainer(elRef: ElementRef<HTMLDivElement>) {
        const scrollObservable = fromEvent(elRef.nativeElement, 'scroll').pipe(
            // pluck<EventTarget, number>('target', 'scrollTop'),
            map((e) => e.target['scrollTop'])
        );
        this.uiService.setScrollObservable(scrollObservable);
    }

    _modalState$ = new ReplaySubject(1);
    activateModal(isActive = true) {
        this.isModalActive = isActive;
        this._modalState$.next(isActive);
    }
    activateDialog(isActive = true) {
        this.isDialogActive = isActive;
        this._modalState$.next(isActive);
    }

    public printLayout$ = this.printService.isPrinting$;

    constructor(
        public overlayContainer: OverlayContainer,
        public renderer: Renderer2,
        public zone: NgZone,
        httpClient: HttpClient,
        @Inject(DOCUMENT) private _document: HTMLDocument,
        private printService: SbPrintService,
        public session: SessionQuery,
        public sessionService: SessionService,
        public ui: UiQuery,
        public uiService: UiService,
        private instrumentService: InstrumentsService,
        private categoryService: CategoryService,
        private accountService: AccountsService,
        private config: AppConfigQuery,
        private settingsQuery: SettingsQuery,
        private localeService: LocaleService,
        private keycloak: KeycloakService,
        private userSettingsQuery: UserSettingsQuery,
        private _router: Router
    ) {}

    animation$ = combineLatest([this._modalState$, this.ui.viewportSize$]).pipe(
        map(([isActive, viewportSize]) =>
            isActive
                ? viewportSize === 'xs'
                    ? 'mobileShow'
                    : 'desktopShow'
                : viewportSize === 'xs'
                ? 'mobileHide'
                : 'desktopHide'
        ),
        shareReplay(1)
    );

    ngOnInit() {
        const appConfig = this.config.get();
        this.loggedIn$ = appConfig.useLatestKeycloakFlow
            ? from(this.keycloak.isLoggedIn())
            : this.session.isAuthenticated$;
        // Setting
        const themeClasses = appConfig.cssTheme
            ? appConfig.cssTheme.split(' ')
            : ['light-theme'];
        themeClasses.forEach((tc) =>
            this.overlayContainer.getContainerElement().classList.add(tc)
        );
        this.renderer.addClass(
            this.renderer.parentNode(this._document.body),
            themeClasses[0]
        );

        this._document.title = appConfig.appTitle;

        // TODO: remove this when we pipes etc have been updated to use local from settings
        registerLocaleData(sv);

        this.userSettingsQuery
            .selectGlobalSetting$('BASE_CURRENCY')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((ccy) => {
                this.uiService.setCurrentCurrency(ccy);
            });

        // if default theme is light or dark, update theme based on settings
        // TODO: refactor and populate available themes like naps in settings
        if (themeClasses[0] === 'light-theme' || themeClasses[0] === 'dark-theme') {
            this.userSettingsQuery
                .selectGlobalSetting$('THEME')
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((theme) => {
                    this.renderer.setAttribute(
                        this.renderer.parentNode(this._document.body),
                        'class',
                        `${theme}-theme`
                    );

                    // get current theme applied to overlay container
                    const themeClass = Array.from(
                        this.overlayContainer.getContainerElement().classList
                    ).find((className) => className.indexOf('-theme') > -1);

                    // remove current theme from overlay container
                    this.overlayContainer
                        .getContainerElement()
                        .classList.remove(themeClass);

                    // add new theme to overlay container
                    this.overlayContainer
                        .getContainerElement()
                        .classList.add(`${theme}-theme`);
                });
        }
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
